import { getCycleRiskLabel } from './functional';

function clampRiskScore(n) {
  if (!Number.isFinite(n)) return 5;
  return Math.min(10, Math.max(1, n));
}

function getCategoryColor(category) {
  switch (category) {
    case 'HIGH':
      return 'text-red-600 bg-red-100';
    case 'MODERATE':
      return 'text-yellow-600 bg-yellow-100';
    case 'LOW':
      return 'text-green-600 bg-green-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

function getSliderBackground() {
  return `linear-gradient(to right,
    rgb(34, 197, 94) 0%,
    rgb(34, 197, 94) 33%,
    rgb(250, 204, 21) 33%,
    rgb(250, 204, 21) 67%,
    rgb(239, 68, 68) 67%,
    rgb(239, 68, 68) 100%)`;
}

/**
 * Materiality-scale risk control (1.0–10.0 slider, bands) for SOX cycle creation only.
 * Module 1 assessment keeps its own inline modal UI in Materiality.js.
 */
const BAND_PRESET_SCORE = { LOW: 2.0, MODERATE: 5.0, HIGH: 8.0 };

export default function MaterialityRiskScoreSlider({
  value,
  onChange,
  disabled = false,
  showTopSummaryRow = true,
  helperText = 'Drag the slider to adjust the risk score.',
  showPresets = true,
  compareToScore = null,
  /** When true, LOW / MODERATE / HIGH tiles act as preset picks (same scores as the pill row). */
  bandTilesSelectable = false
}) {
  const score = clampRiskScore(value);
  const category = getCycleRiskLabel(score) || 'LOW';

  const handleSliderChange = (e) => {
    const next = parseFloat(e.target.value);
    if (Number.isFinite(next)) onChange(next);
  };

  const tileBase =
    'text-center py-2 rounded w-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-900';
  const tileInteractive = bandTilesSelectable
    ? 'cursor-pointer hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50'
    : '';

  const renderBandTile = (band, label, rangeText) => {
    const active = category === band;
    const bgClass =
      band === 'LOW'
        ? active
          ? 'bg-green-100 border-2 border-green-500'
          : 'bg-gray-50 border border-transparent'
        : band === 'MODERATE'
          ? active
            ? 'bg-yellow-100 border-2 border-yellow-500'
            : 'bg-gray-50 border border-transparent'
          : active
            ? 'bg-red-100 border-2 border-red-500'
            : 'bg-gray-50 border border-transparent';

    const inner = (
      <>
        <div
          className={`text-xs font-medium ${
            band === 'LOW'
              ? active
                ? 'text-green-700'
                : 'text-gray-500'
              : band === 'MODERATE'
                ? active
                  ? 'text-yellow-700'
                  : 'text-gray-500'
                : active
                  ? 'text-red-700'
                  : 'text-gray-500'
          }`}
        >
          {label}
        </div>
        <div
          className={`text-xs ${
            band === 'LOW'
              ? active
                ? 'text-green-600'
                : 'text-gray-400'
              : band === 'MODERATE'
                ? active
                  ? 'text-yellow-600'
                  : 'text-gray-400'
                : active
                  ? 'text-red-600'
                  : 'text-gray-400'
          }`}
        >
          {rangeText}
        </div>
      </>
    );

    if (bandTilesSelectable) {
      const preset = BAND_PRESET_SCORE[band];
      return (
        <button
          key={band}
          type="button"
          disabled={disabled}
          onClick={() => onChange(preset)}
          aria-pressed={active}
          aria-label={`${label} risk band, set score to ${preset}`}
          className={`${tileBase} ${tileInteractive} ${bgClass}`}
        >
          {inner}
        </button>
      );
    }

    return (
      <div key={band} className={`${tileBase} ${bgClass}`}>
        {inner}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {showTopSummaryRow && (
        <div className="flex justify-between items-center gap-2">
          <label className="text-sm font-medium text-gray-700 shrink-0">
            Adjust risk score
          </label>
          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <span className="text-sm text-gray-600 hidden sm:inline">Score:</span>
            <span
              className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(category)}`}
            >
              {category}
            </span>
            <span className="text-2xl font-bold text-gray-900 tabular-nums">
              {score.toFixed(1)}
            </span>
            {compareToScore != null &&
              Number.isFinite(compareToScore) &&
              score !== compareToScore && (
                <span className="text-sm text-gray-500">
                  (was {compareToScore.toFixed(1)})
                </span>
              )}
          </div>
        </div>
      )}

      <div className="relative">
        <input
          type="range"
          min="1.0"
          max="10.0"
          step="0.1"
          value={score}
          disabled={disabled}
          onChange={handleSliderChange}
          className="w-full risk-slider"
          style={{ background: getSliderBackground() }}
          aria-label="Risk score from 1.0 (low) to 10.0 (high), Module 1 materiality scale"
          aria-valuemin={1}
          aria-valuemax={10}
          aria-valuenow={Number(score.toFixed(1))}
          aria-valuetext={`${score.toFixed(1)}`}
        />
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>1.0</span>
          <span>3.3</span>
          <span>6.6</span>
          <span>10.0</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {renderBandTile('LOW', 'LOW', '1.0 - 3.3')}
        {renderBandTile('MODERATE', 'MODERATE', '3.4 - 6.6')}
        {renderBandTile('HIGH', 'HIGH', '6.7 - 10.0')}
      </div>

      {helperText ? (
        <div className="text-xs text-gray-500 text-center">{helperText}</div>
      ) : null}

      {showPresets && !bandTilesSelectable && !disabled ? (
        <div className="flex justify-center flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange(2.0)}
            className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
          >
            Low (2.0)
          </button>
          <button
            type="button"
            onClick={() => onChange(5.0)}
            className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full hover:bg-yellow-200 transition-colors"
          >
            Moderate (5.0)
          </button>
          <button
            type="button"
            onClick={() => onChange(8.0)}
            className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
          >
            High (8.0)
          </button>
        </div>
      ) : null}
    </div>
  );
}
